/**
 * 手機端：獎期、開獎號碼（與 member/period、member/lastResult 對接）
 */
import {
  memberPeriod,
  memberLastResult,
  memberOdds,
  memberInfo,
  memberAccounts,
  memberHistory,
  memberDayResult,
  memberDresult,
  memberBets,
} from './memberRestApi'
import { GAME_CODE_TO_LOTTERY, MOBI_LOTTERY_TITLE_TO_CODE } from './memberLotteryIds'
import {
  normalizeMemberCreditProfile,
  mergeMemberAccountsArray,
  mapPlayLimitsToMobiLimitRows,
  pickTodayWinLossFromMemberRaw,
} from '@/utils/memberCreditProfile'
import { isFantanLottery } from '@/data/gamePlayFantan.js'
import { isPk10Lottery } from '@/data/gamePlayPk10.js'
import { isCanada20Lottery } from '@/data/gamePlaySscMian.js'
import { parseSealSecondsFromPeriod } from '@/utils/memberPeriodSeal'

const ODDS_GAMES_PK10 =
  'DX1,DX2,DX3,DX4,DX5,DX6,DX7,DX8,DX9,DX10,DS1,DS2,DS3,DS4,DS5,DS6,DS7,DS8,DS9,DS10,LH1,LH2,LH3,LH4,LH5,GDX,GDS,B1,B2,B3,B4,B5,B6,B7,B8,B9,B10,GYH,DLDH,XYLH,XYLH8'
const ODDS_GAMES_SSC =
  'B1,B2,B3,B4,B5,DX1,DX2,DX3,DX4,DX5,DS1,DS2,DS3,DS4,DS5,LH1,GDX,GDS,GYH,ZDS,ZDX,LH,TS0,TS1,TS2'
const ODDS_GAMES_JND2 = (() => {
  const hz = Array.from({ length: 28 }, (_, i) => 'HZ_' + i)
  const balls = [1, 2, 3].flatMap((b) => Array.from({ length: 10 }, (_, d) => `B${b}_${d}`))
  const rest = [
    'DX1',
    'DX2',
    'DX3',
    'DS1',
    'DS2',
    'DS3',
    'DX_D',
    'DX_X',
    'DS_D',
    'DS_S',
    'JZDX_D',
    'JZDX_X',
    'DXDS_DD',
    'DXDS_DS',
    'DXDS_XD',
    'DXDS_XS',
    'BZ_BZ',
    'BZ_DZ',
    'BZ_SZ',
    'LH_L',
    'LH_H',
    'LH_T',
  ]
  return [...rest, ...hz, ...balls].join(',')
})()
const ODDS_GAMES_FT = 'GDX,GDS,B1,B2,B3,B4,DS_D,DS_S,SM,FTT,FTZ,FTJ,FTN,FTF'

function normalizeOddsPayload(raw) {
  if (raw == null || typeof raw !== 'object') return {}
  if (Array.isArray(raw)) return {}
  return raw.odds && typeof raw.odds === 'object' && !Array.isArray(raw.odds) ? raw.odds : raw
}

export function oddsGamesParamForLotteryTitle(title) {
  if (isPk10Lottery(title)) return ODDS_GAMES_PK10
  if (isFantanLottery(title)) return ODDS_GAMES_FT
  if (isCanada20Lottery(title)) return ODDS_GAMES_JND2
  return ODDS_GAMES_SSC
}

/**
 * @returns {Promise<Record<string, number>>}
 */
export async function fetchMemberOddsFlat(lotteryTitle) {
  const code = MOBI_LOTTERY_TITLE_TO_CODE[lotteryTitle]
  const lottery = code && GAME_CODE_TO_LOTTERY[code]
  if (!lottery) return {}
  const games = oddsGamesParamForLotteryTitle(lotteryTitle)
  const data = await memberOdds({ lottery, games })
  return normalizeOddsPayload(data)
}

function parseBalls(resultStr) {
  const s = String(resultStr || '').trim()
  if (!s) return []
  const parts = s.split(/[,，]/).map((x) => x.trim()).filter(Boolean)
  return parts.map((p) => {
    const n = parseInt(p, 10)
    return Number.isFinite(n) ? n : p
  })
}

function fmtMmSs(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0))
  const m = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${m}:${ss}`
}

/**
 * 本地每秒遞減封盤倒計時（與接口輪詢互補；接口返回會覆寫為服務端值）。
 * @param {object|null} draw fetchMemberDrawState 返回值
 */
export function tickMemberDrawState(draw) {
  if (!draw || typeof draw !== 'object') return draw
  const prevSeal = Math.floor(Number(draw.sealSecondsLeft) || 0)
  const prevOpen = Math.floor(Number(draw.openSecondsLeft) || 0)
  if (Number.isFinite(prevSeal) && prevSeal > 0) {
    const left = prevSeal - 1
    draw.sealSecondsLeft = left
    draw.sealCountdown = fmtMmSs(left)
    draw.nextDrawText = `${left}秒`
  }
  if (Number.isFinite(prevOpen) && prevOpen > 0) {
    draw.openSecondsLeft = prevOpen - 1
    draw.openCountdown = fmtMmSs(prevOpen - 1)
  }
  return draw
}

function fmtTimeFromMs(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return ''
  const d = new Date(Number(ms))
  if (Number.isNaN(d.getTime())) return ''
  const z = (n) => String(n).padStart(2, '0')
  return `${z(d.getHours())}:${z(d.getMinutes())}:${z(d.getSeconds())}`
}

/**
 * @param {string} lotteryTitle 與 lotteryTypes / 大廳 title 一致
 * @returns {Promise<null | {
 *   lastPeriod: string,
 *   currentPeriod: string,
 *   nums: number[],
 *   sealCountdown: string,
 *   nextDrawText: string,
 *   drawTime: string,
 *   sealSecondsLeft: number,
 *   fantanBalls: number[],
 *   fantanFan: string|number,
 * }>}
 */
export async function fetchMemberDrawState(lotteryTitle) {
  const code = MOBI_LOTTERY_TITLE_TO_CODE[lotteryTitle]
  const lottery = code && GAME_CODE_TO_LOTTERY[code]
  if (!lottery) return null

  const [period, last] = await Promise.all([
    memberPeriod({ lottery }),
    memberLastResult({ lottery }),
  ])

  const nums = parseBalls(last && last.result)
  const lastPeriod = String((last && last.drawNumber) || (period && period.pnumber) || '')
  const currentPeriod = String((period && period.drawNumber) || '')
  const ct = period && period.currentTime
  const cl = period && period.closeTime
  const hasScheduleTime =
    ct != null &&
    cl != null &&
    Number.isFinite(Number(ct)) &&
    Number.isFinite(Number(cl)) &&
    Number(cl) > Number(ct)
  const parsedSeal = period ? parseSealSecondsFromPeriod(period) : null
  const hasParsedSeal = parsedSeal != null && Number.isFinite(Number(parsedSeal)) && Number(parsedSeal) >= 0
  let closeSec = 0
  if (hasScheduleTime) {
    closeSec = Math.max(0, Math.floor((Number(cl) - Number(ct)) / 1000))
  } else if (hasParsedSeal) {
    closeSec = Math.max(0, Math.floor(Number(parsedSeal)))
  }
  let openSec = 0
  let drawMs
  if (period && ct != null && Number.isFinite(Number(ct))) {
    const cur = Number(ct)
    // 开奖时间
    drawMs =
      period.drawTime ??
      period.draw_time ??
      period.kaijiang_time ??
      period.nextDrawTime ??
      period.next_draw_time
    if (drawMs != null && Number.isFinite(Number(drawMs)) && Number(drawMs) > cur) {
      openSec = Math.floor((Number(drawMs) - cur) / 1000)
    }
  }
  // 距开盘秒数（openTime）
  let openTimeSec = 0
  if (period && ct != null && Number.isFinite(Number(ct))) {
    const cur = Number(ct)
    const ot = period.openTime ?? period.open_time
    if (ot != null && Number.isFinite(Number(ot)) && Number(ot) > cur) {
      openTimeSec = Math.floor((Number(ot) - cur) / 1000)
    }
  }
  const hasScheduleTimeFinal = hasScheduleTime || hasParsedSeal
  const sealCountdown = fmtMmSs(closeSec)
  const openCountdown = fmtMmSs(openSec)
  const nextDrawText = `${closeSec}秒`
  const drawTime = fmtTimeFromMs(drawMs) || fmtTimeFromMs(ct) || fmtTimeFromMs(Date.now())

  const base = {
    lastPeriod,
    currentPeriod,
    nums,
    sealCountdown,
    openCountdown,
    nextDrawText,
    drawTime,
    /** 距封盤剩餘秒；≤0 視為已封盤，前端賠率顯示為空 */
    sealSecondsLeft: closeSec,
    /** 距开奖剩餘秒 */
    openSecondsLeft: openSec,
    /** 距开盘剩餘秒 */
    openTimeSecondsLeft: openTimeSec,
    hasScheduleTime: hasScheduleTimeFinal,
    fantanBalls: [],
    fantanFan: nums[0] != null ? nums[0] : '',
  }

  if (isFantanLottery(lotteryTitle)) {
    const other = last && last.resultOther
    let codes = []
    if (typeof other === 'string' && other) {
      codes = other.split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n))
    }
    base.fantanBalls = codes.length >= 8 ? codes.slice(0, 8) : nums.length ? nums : [0, 0, 0, 0, 0, 0, 0, 0]
    base.fantanFan = nums[0] != null ? nums[0] : 1
    return base
  }

  if (isPk10Lottery(lotteryTitle)) {
    base.nums = nums
    return base
  }

  base.nums = nums.length >= 5 ? nums.slice(0, 5) : nums
  return base
}

/**
 * 信用資料頁：member/info（必填 lottery）+ 失敗時 member/accounts
 * @param {string} [lotteryTitle] 大廳彩種名，如 极速时时彩
 */
export async function fetchMobiCreditPage(lotteryTitle) {
  const title = (lotteryTitle || '极速时时彩').trim()
  const code = MOBI_LOTTERY_TITLE_TO_CODE[title]
  const lottery = (code && GAME_CODE_TO_LOTTERY[code]) || GAME_CODE_TO_LOTTERY.ssc
  let raw = null
  try {
    raw = await memberInfo({ lottery })
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) console.warn('[mobi] member/info', e)
  }
  if (
    raw == null ||
    (typeof raw === 'object' && !Array.isArray(raw) && !raw.member && Object.keys(raw).length === 0)
  ) {
    try {
      const acc = await memberAccounts()
      if (Array.isArray(acc)) {
        raw = mergeMemberAccountsArray(acc) || {}
      } else if (acc && typeof acc === 'object') {
        raw = acc
      }
    } catch (e2) {
      if (typeof console !== 'undefined' && console.warn) console.warn('[mobi] member/accounts', e2)
    }
  }
  if (!raw || typeof raw !== 'object') return null
  const credit = normalizeMemberCreditProfile(raw)
  const pan =
    raw.member && raw.member.panlu != null ? String(raw.member.panlu).trim() : 'A'
  let limitRows = null
  if (Array.isArray(raw.play_limits) && raw.play_limits.length) {
    limitRows = mapPlayLimitsToMobiLimitRows(raw.play_limits)
  }
  let todayWin = pickTodayWinLossFromMemberRaw(raw)
  if (todayWin == null) {
    try {
      const acc = await memberAccounts()
      if (Array.isArray(acc)) {
        for (let i = 0; i < acc.length; i++) {
          const item = acc[i]
          const resVal = item.result ?? item.todayResult ?? item.today_win ?? item.todayWin
          if (resVal != null && String(resVal).trim() !== '') {
            todayWin = String(resVal)
            break
          }
        }
      }
    } catch (e3) {
      if (typeof console !== 'undefined' && console.warn) console.warn('[mobi] member/accounts supplement', e3)
    }
  }
  return { credit, pan, limitRows, todayWin }
}

/** 與 Login.vue 一致：默認走會員接口；僅在顯式關閉時為 false */
export function useMemberRestEnv() {
  try {
    const env = typeof process !== 'undefined' && process.env ? process.env : {}
    const raw = env.VUE_APP_USE_MEMBER_REST
    const flag = raw == null || raw === '' ? '' : String(raw).trim().toLowerCase()
    if (flag === '0' || flag === 'false' || flag === 'off') return false
    return true
  } catch (e) {
    return true
  }
}

function extractList(raw) {
  if (Array.isArray(raw)) return raw
  if (!raw || typeof raw !== 'object') return []
  const keys = ['list', 'rows', 'data', 'items', 'records', 'results', 'resultList']
  for (const k of keys) {
    if (Array.isArray(raw[k])) return raw[k]
  }
  if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    for (const k of keys) {
      if (Array.isArray(raw.data[k])) return raw.data[k]
    }
  }
  return []
}

function toHistoryRow(item, kind) {
  if (!item || typeof item !== 'object') return null
  const period = String(
    item.qishu ?? item.drawNumber ?? item.period ?? item.qihao ?? item.issue ?? item.expect ?? item.pnumber ?? '',
  ).trim()
  if (!period) return null
  const resultRaw =
    item.result ?? item.openCode ?? item.open_code ?? item.openNumber ?? item.kj ?? item.haoma ?? (Array.isArray(item.balls) ? item.balls.join(',') : '')
  const nums = parseBalls(resultRaw)
  if (!nums.length) return null
  if (kind === 'pk10') {
    const sum = Number(nums[0] || 0) + Number(nums[1] || 0)
    const longHu = [0, 1, 2, 3, 4].map((i) =>
      Number(nums[i] || 0) >= Number(nums[9 - i] || 0) ? '龙' : '虎',
    )
    return {
      period,
      nums: nums.slice(0, 10),
      guanyaSum: sum,
      guanyaBig: sum >= 12,
      guanyaOdd: sum % 2 === 1,
      longHu,
    }
  }
  if (kind === 'ca20') {
    const balls = nums.slice(0, 3).map((b) => Number(b || 0))
    const sum = balls.reduce((a, b) => a + b, 0)
    const isBaozi = balls.length === 3 && balls[0] === balls[1] && balls[1] === balls[2]
    return {
      period,
      nums: balls,
      sum,
      sumBig: sum >= 14,
      sumOdd: sum % 2 === 1,
      longHu: Number(balls[0] || 0) > Number(balls[2] || 0) ? '龙' : Number(balls[0] || 0) < Number(balls[2] || 0) ? '虎' : '和',
      extreme: sum >= 22 && sum <= 27 ? '极大' : sum >= 0 && sum <= 5 ? '极小' : '',
      baozi: isBaozi ? '豹子' : '',
    }
  }
  const balls = nums.slice(0, 5)
  const sum = balls.reduce((a, b) => a + Number(b || 0), 0)
  const wan = Number(balls[0] || 0)
  const ge = Number(balls[4] || 0)
  let longHu = '和'
  if (wan > ge) longHu = '龙'
  else if (wan < ge) longHu = '虎'
  return {
    period,
    nums: balls,
    sum,
    sumBig: sum >= 23,
    sumOdd: sum % 2 === 1,
    longHu,
  }
}

export async function fetchMemberDrawHistoryRows(lotteryTitle, dateText, page = 1, pageSize = 30) {
  const code = MOBI_LOTTERY_TITLE_TO_CODE[lotteryTitle]
  const lottery = code && GAME_CODE_TO_LOTTERY[code]
  if (!lottery) return { rows: [], hasMore: false, total: 0 }
  const kind = isPk10Lottery(lotteryTitle) ? 'pk10' : isCanada20Lottery(lotteryTitle) ? 'ca20' : 'ssc'
  const pg = Math.max(1, Number(page) || 1)
  const ps = Math.max(1, Number(pageSize) || 30)
  let all = []
  let totalFromServer = 0
  try {
    const params = { lottery, page: pg, pageSize: ps }
    if (dateText) params.date = dateText
    const raw = await memberDresult(params)
    all = extractList(raw).map((x) => toHistoryRow(x, kind)).filter(Boolean)
    const rawResp = raw && raw.raw
    const dataInfo = rawResp && rawResp.data_info
    if (dataInfo) {
      totalFromServer = Number(dataInfo.total_history) || all.length
    } else {
      totalFromServer = all.length
    }
  } catch (_) {
    all = []
  }
  const total = totalFromServer || all.length
  const start = (pg - 1) * ps
  const rows = all
  return { rows, hasMore: start + ps < total, total }
}

function mapMobiBetRows(list, page) {
  const pg = page == null ? 1 : Number(page) || 1
  return list.map((row, i) => {
    const v = row.amount ?? row.betAmount ?? row.money ?? row.stake ?? row.total
    const n = Number(v || 0)
    const odds = (row.odds || row.odd || row.rate || row.pl || row.peilv || row.oddsValue || '--').toString().trim() || '--'
    const mingxiKeys = Object.keys(row).filter((k) => /^mingxi_\d+$/.test(k)).sort()
    const mingxi = mingxiKeys.map((k) => String(row[k] ?? '').trim()).filter(Boolean).join(',')
    let play = (row.playName || row.game || row.contents || row.content || row.betInfo || '-').toString().trim() || '-'
    const titleKey = (row.title || row.key || row.betKey || '').toString().trim()
    if (titleKey && (titleKey.includes(':') || /^(pk10|ssc|ball|total|dt):/i.test(titleKey))) {
      play = titleKey
    } else {
      const mingxiFallback = mingxi || '-'
      if (play === '-' && mingxiFallback !== '-') play = mingxiFallback
    }
    const draw = row.drawNumber ?? row.period ?? row.qihao ?? row.qishu ?? '-'
    const tm = row.time ?? row.betTime ?? row.createTime ?? row.create_time ?? row.date ?? '--'
    return {
      id: row.id ?? row.orderId ?? row.no ?? `p${pg}-${i}-${String(draw)}`,
      drawNumber: String(draw),
      play,
      odds,
      amount: n,
      time: String(tm),
      lotteryName: String(row.lotteryName || row.gameName || '').trim(),
      raw: row,
    }
  })
}

function pickBetListTotalHint(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  const v =
    payload.total ??
    payload.Total ??
    payload.totalCount ??
    payload.totalRows ??
    payload.count ??
    payload.TotalCount ??
    payload.totalSize ??
    payload.recordCount ??
    payload.totalRecords ??
    payload.total_size ??
    payload.recordsTotal
  if (v == null || String(v).trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * 下注明細分頁（member/bets）
 * @returns {{ rows: object[], hasMore: boolean, totalHint: number|null }}
 */
export async function fetchMobiBetPage(lotteryTitle, page, pageSize) {
  const code = MOBI_LOTTERY_TITLE_TO_CODE[lotteryTitle]
  const lottery = code && GAME_CODE_TO_LOTTERY[code]
  if (!lottery) return { rows: [], hasMore: false, totalHint: null }
  const p = Math.max(1, Number(page) || 1)
  const ps = Math.min(100, Math.max(1, Number(pageSize) || 20))
  const data = await memberBets({
    // lottery,
    page: p,
    pageSize: ps,
    rows: ps,
    size: ps,
  })
  const list = extractList(data)
  const totalHint = pickBetListTotalHint(data)
  const rows = mapMobiBetRows(list, p)
  let hasMore = false
  if (totalHint != null && totalHint > list.length) {
    hasMore = p * list.length < totalHint
  } else {
    hasMore = list.length > 0
  }
  return { rows, hasMore, totalHint }
}

/** 單次拉取較多條（兼容舊調用） */
export async function fetchMobiBetSummary(lotteryTitle) {
  const code = MOBI_LOTTERY_TITLE_TO_CODE[lotteryTitle]
  const lottery = code && GAME_CODE_TO_LOTTERY[code]
  if (!lottery) return { totalCount: 0, totalAmount: '0.00', rows: [] }
  const { rows } = await fetchMobiBetPage(lotteryTitle, 1, 100)
  const amount = rows.reduce((s, r) => s + Number(r.amount || 0), 0)
  return { totalCount: rows.length, totalAmount: amount.toFixed(2), rows }
}

/**
 * 調用 /member/history → 後端返回預聚合的兩周報表（含所有彩種合計）
 * @returns {{ lastWeek: Array, thisWeek: Array, summary: object }|null}
 */
export async function fetchMobiSettlementWeekReport() {
  try {
    const data = await memberHistory({})
    if (!data || typeof data !== 'object') return null
    const lastWeek = Array.isArray(data.last_week) ? data.last_week : []
    const thisWeek = Array.isArray(data.this_week) ? data.this_week : []
    return {
      lastWeek: lastWeek.map(normalizeSettlementDay),
      thisWeek: thisWeek.map(normalizeSettlementDay),
      summary: data.summary || null,
    }
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) console.warn('[mobi] settlement week report', e)
    return null
  }
}

function normalizeSettlementDay(r) {
  return {
    date: r.date || '',
    orders: Number(r.count) || 0,
    bet: Number(r.money) || 0,
    result: Number(r.sjwin) || 0,
  }
}

export async function fetchMobiBetsRawForDateRange(lotteryTitle, settled, beginDate, endDate, page = 1, pageSize = 15) {
  const code = MOBI_LOTTERY_TITLE_TO_CODE[lotteryTitle]
  const lottery = code && GAME_CODE_TO_LOTTERY[code]
  const params = {
    settled: !!settled,
    page: Number(page) || 1,
    pageSize: Number(pageSize) || 15,
    rows: Number(pageSize) || 15,
    size: Number(pageSize) || 15
  }

  // 如果有 lottery 则传，没有则不传
  if (lottery) {
    params.lottery = lottery
  }

  if (beginDate && endDate && beginDate === endDate) {
    params.date = beginDate
  } else {
    if (beginDate) { params.beginDate = beginDate; params.begin = beginDate }
    if (endDate) { params.endDate = endDate; params.end = endDate }
  }

  try {
    const response = await memberBets(params);
    return response; // 必须返回完整对象，以便 Vue 组件读取 summary 字段
  } catch (e) {
    return { data: { list: [], summary: [] } };
  }
}

async function fetchAllMemberBetsForReport(lottery, settled, beginDate, endDate) {
  const paramsBase = { lottery, settled: !!settled }
  if (beginDate && endDate && beginDate === endDate) {
    paramsBase.date = beginDate
  } else {
    if (beginDate) {
      paramsBase.beginDate = beginDate
      paramsBase.begin = beginDate
    }
    if (endDate) {
      paramsBase.endDate = endDate
      paramsBase.end = endDate
    }
  }
  const all = []
  let page = 1
  const pageSize = 200
  for (let guard = 0; guard < 30; guard++) {
    const data = await memberBets({ ...paramsBase, page, pageSize, rows: pageSize, size: pageSize })
    const list = extractList(data)
    if (!list.length) break
    all.push(...list)
    const totalHint = pickBetListTotalHint(data)
    if (totalHint != null && totalHint > list.length && all.length >= totalHint) break
    page++
  }
  return all
}

function aggregateMobiBetsByDates(list, dateSet, settled) {
  const map = {}
  dateSet.forEach((d) => {
    map[d] = { orders: 0, bet: 0, result: 0 }
  })
  const pickStake = (row) => {
    const v = row.amount ?? row.betAmount ?? row.money ?? row.stake ?? row.total
    return Number(v || 0) || 0
  }
  const pickResult = (row) => {
    const v = settled
      ? row.result ?? row.winAmount ?? row.profit ?? row.resultAmount ?? row.settleAmount
      : row.result ?? row.winAmount ?? row.profit ?? 0
    return Number(v || 0) || 0
  }
  const extractYMD = (row) => {
    const raw = row.betDate ?? row.date ?? row.createTime ?? row.create_time ?? row.time ?? row.betTime
    const s = String(raw ?? '').trim()
    const m = s.match(/(\d{4}-\d{2}-\d{2})/)
    return m ? m[1] : ''
  }
  for (let i = 0; i < list.length; i++) {
    const row = list[i]
    const d = extractYMD(row)
    if (!d || !map[d]) continue
    map[d].orders += 1
    map[d].bet += pickStake(row)
    map[d].result += pickResult(row)
  }
  return map
}

export function extractBetDateYmdRow(row) {
  const raw =
    row.betDate ?? row.date ?? row.createTime ?? row.create_time ?? row.time ?? row.betTime
  const s = String(raw ?? '').trim()
  const m = s.match(/(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}

export function pickSettlementCategoryLabel(row) {
  console.log(row)
  const name = row.lotteryName || row.gameName || ''
  const s = String(name || '').trim()
  if (s) return s.slice(0, 80)
  const play =
    row.playName ||
    row.gameName ||
    row.contents ||
    row.content ||
    row.betInfo ||
    row.title ||
    ''
  const p = String(play || '').trim()
  if (p) {
    const first = p.split(/[,，]/)[0].trim()
    return first.slice(0, 80) || '-'
  }
  const game = row.game || row.gameCode || row.playCode || ''
  const g = String(game || '').trim()
  return g || '-'
}

/** 結算分類注單列表：與 aggregateMobiBetsByCategoryForDay 同一分類鍵 */
export function filterMobiBetsBySettlementCategoryForDay(list, dateYmd, categoryLabel) {
  const cat = String(categoryLabel || '').trim()
  const d = String(dateYmd || '').trim()
  if (!d || !cat) return []
  return list.filter((row) => extractBetDateYmdRow(row) === d && pickSettlementCategoryLabel(row) === cat)
}

/**
 * 結算分類明細：按玩法/類型匯總單日（數據來源 GET /member/bets + settled）
 */
export function aggregateMobiBetsByCategoryForDay(summary) {
  // 確保傳入的是數組，如果不是則默認為空數組
  const summaryList = Array.isArray(summary) ? summary : [];

  // 1. 將 summary 數據映射為頁面表格需要的字段格式
  const rows = summaryList.map((item) => ({
    typeLabel: item.lotteryName || item.g_type || '-', // 顯示彩種名稱
    orders: Number(item.total_count) || 0,            // 筆數
    bet: Number(item.total_amount) || 0,             // 下注總額
    result: Number(item.total_result) || 0,          // 實際結果
  })).sort((a, b) =>
      String(a.typeLabel).localeCompare(String(b.typeLabel), 'zh-Hans-CN')
  );

  // 2. 計算當前頁合計
  let totalOrders = 0;
  let totalBet = 0;
  let totalResult = 0;

  rows.forEach((r) => {
    totalOrders += r.orders;
    totalBet += r.bet;
    totalResult += r.result;
  });

  return {
    rows,
    totalOrders,
    totalBet: Math.round(totalBet * 100) / 100,
    totalResult: Math.round(totalResult * 100) / 100,
  };
}

/**
 * 結算/未結報表：按交易日匯總（GET /member/bets，與 PC 未結/已結一致帶 settled + 日期區間）
 * @param {string} lotteryTitle 大廳彩種名
 * @param {boolean} settled true=已結 false=未結
 * @param {string[]} dates 須匯總的 YYYY-MM-DD 列表（通常為上週或本週各 7 天）
 * @returns {Promise<Array<{ date: string, orders: number, bet: number, result: number }>>}
 */
export async function fetchMobiBetsAggregatedByDate(lotteryTitle, settled, dates) {
  const code = MOBI_LOTTERY_TITLE_TO_CODE[lotteryTitle]
  const lottery = code && GAME_CODE_TO_LOTTERY[code]
  const empty = dates.map((d) => ({ date: d, orders: 0, bet: 0, result: 0 }))
  if (!lottery || !dates.length) return empty
  const sorted = [...dates].sort()
  const beginDate = sorted[0]
  const endDate = sorted[sorted.length - 1]
  const list = await fetchAllMemberBetsForReport(lottery, settled, beginDate, endDate)
  const set = new Set(dates)
  const agg = aggregateMobiBetsByDates(list, set, settled)
  return dates.map((d) => ({
    date: d,
    orders: agg[d].orders,
    bet: Math.round(agg[d].bet * 100) / 100,
    result: Math.round(agg[d].result * 100) / 100,
  }))
}
